import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useResolvedPath } from 'react-router-dom';

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import { useResizeObserver } from 'usehooks-ts';

import { Input, Table, Tooltip, Modal, Flex, notification, InputNumber, Select, Form, Switch } from 'antd';
import { ColumnsType } from 'antd/es/table';
import {
    LoadingOutlined,
    ReloadOutlined,
    UnorderedListOutlined,
    PrinterOutlined,
    UsergroupAddOutlined,
    BarcodeOutlined,
    ExclamationCircleOutlined,
    CheckOutlined,
    QuestionCircleOutlined,
    RollbackOutlined,
} from '@ant-design/icons';

import { usePDF } from '@react-pdf/renderer';
import printJS from 'print-js';

import { v4 as uuid } from 'uuid';

import IBaseComponentProps from '@components/base-component-props';
import ConsignmentCard, { IBoxGroupCardItem } from '@components/consignment-card/consignment-card';

import Processing from '@controls/processing';
import Filter from '@controls/filter/filter';
import Toolbar from '@controls/toolbar/toolbar';
import { Tags, TransitWarehouseTag } from '@controls/tags';

import { useAppDispatch, useAppSelector } from '@store/hooks';

import BarcodePdf from '@flp/print-forms/barcode-pdf';
import TruckAcceptancePdf from '@print-forms/truck-acceptance/truck-acceptance-pdf';

import { delayAction, getDate, getEnumList } from '@extensions/utils';
import { exception, securityRestriction, error, success } from '@extensions/notification';
import { userLoaded, setFilter } from '@store/actions';

import { IUserSession } from '@entities/user-session';
import { ITruckFilter } from '@entities/truck-filter';
import { ITruck } from '@entities/truck';
import { ICountry } from '@entities/country';
import { IConsignment } from '@entities/consignment';
import { ICity } from '@entities/city';

import { Permission, hasPermission } from '@enums/permission';
import { AreaType } from '@enums/area-type';
import { enumLabel as jobStatusLabel } from '@enums/job-status';
import { WarehouseType } from '@enums/warehouse-type';
import { TruckType } from '@enums/truck-type';
import { TruckStatus, enumLabel as truckStatusLabel } from '@enums/truck-status';
import { IEnumItem } from '@enums/enum-item';

dayjs.extend(utc);

const filterContext: string = 'Acceptance';

const Acceptance = (props: IBaseComponentProps) => {
    const { serverFetch } = props;

    const initFilter: ITruckFilter = {
        statuses: [TruckStatus.OnWay],
        type: TruckType.Import,
        isArchived: false,
    };

    const containerRef = useRef<HTMLDivElement>(null);
    const { width = 0, height = 0 } = useResizeObserver({
        ref: containerRef,
        box: 'border-box',
    });

    const filterRef = useRef<HTMLDivElement>(null);
    const { width: filterWidth = 0, height: filterHeight = 0 } = useResizeObserver({
        ref: filterRef,
        box: 'border-box',
    });

    const navigate = useNavigate();
    const resolved = useResolvedPath('');

    const [modal, modalContextHolder] = Modal.useModal();

    const d = useAppDispatch();
    const [api, contextHolder] = notification.useNotification();

    const userSession = useAppSelector<IUserSession>((s) => s.userSession);

    const [showFilter, setShowFilter] = useState<boolean>(true);

    const filter = useAppSelector<ITruckFilter>((s) => s.filters[filterContext]);

    const [headers, setHeaders] = useState<Array<ITruck>>([]);
    const [selectedIds, setSelectedIds] = useState<React.Key[]>([]);
    const [currentTruck, setCurrentTruck] = useState<ITruck>();
    const [countries, setCountries] = useState<Array<ICountry>>([]);
    const [refreshRequired, setRefreshRequired] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [statuses] = useState<Array<IEnumItem>>(getEnumList(TruckStatus, truckStatusLabel).filter((s) => s.value != TruckStatus.New));

    const [printData, setPrintData] = useState<any>();
    const [loadingPrintData, setLoadingPrintData] = useState<boolean>(false);
    const [startPrint, setStartPrint] = useState<boolean>(false);

    const [pdfInstance, updatePdf] = usePDF();

    const [startPrintAcceptance, setStartPrintAcceptance] = useState<boolean>(false);
    const [loadingPrintAcceptanceData, setLoadingPrintAcceptanceData] = useState(false);
    const [printAcceptanceData, setPrintAcceptanceData] = useState<any>();
    const [acceptancePdfInstance, updateAcceptancePdf] = usePDF();

    const [expandedCityKeys, setExpandedCityKyes] = useState<Array<React.Key>>([]);

    const [barcodesGenerating, setBarcodesGenerating] = useState<boolean>(false);

    useEffect(() => {
        let cleanup = false;

        if (!refreshRequired) return;

        setCountries([]);
        setHeaders([]);
        setSelectedIds([]);
        setCurrentTruck(undefined);

        const fetchData = async () => {
            setLoading(true);

            const promises = [
                await serverFetch('countries', { method: 'GET' })
                    .then((data: any) => {
                        return data;
                    })
                    .catch((ex: any) => {
                        exception(api, 'Ошибка получения стран', ex, () => d(userLoaded(undefined)));
                    }),
            ];

            promises.push(
                await serverFetch('warehouse/acceptance/trucks', { method: 'GET', queryParams: filter })
                    .then((data: any) => {
                        return data;
                    })
                    .catch((ex: any) => {
                        exception(api, 'Ошибка получения машин', ex, () => d(userLoaded(undefined)));
                    }),
            );

            Promise.all([promises]).then((result) => {
                if (cleanup) return;

                setCountries(result[0][0] || []);

                setHeaders(result[0][1] || []);

                setLoading(false);
                setRefreshRequired(false);
            });
        };

        fetchData();

        return () => {
            cleanup = true;
        };
    }, [refreshRequired]);

    useEffect(() => {
        if (!filter) {
            d(setFilter(initFilter, filterContext));
            return;
        }

        delayAction(() => setRefreshRequired(true));
    }, [filter]);

    useEffect(() => {
        if (printData) {
            updatePdf(<BarcodePdf labels={printData} />);
            setLoadingPrintData(false);
            setStartPrint(true);
        }
    }, [printData]);

    useEffect(() => {
        if (startPrint && !pdfInstance.loading && pdfInstance.blob) {
            setStartPrint(false);
            setPrintData(undefined);

            const blobURL = URL.createObjectURL(pdfInstance.blob);
            printJS(blobURL);
        }
    }, [startPrint, pdfInstance]);

    useEffect(() => {
        if (printAcceptanceData) {
            updateAcceptancePdf(<TruckAcceptancePdf printData={printAcceptanceData} countries={countries} />);
            setLoadingPrintAcceptanceData(false);
            setStartPrintAcceptance(true);
        }
    }, [printAcceptanceData]);

    useEffect(() => {
        if (startPrintAcceptance && !acceptancePdfInstance.loading && acceptancePdfInstance.blob) {
            setStartPrintAcceptance(false);
            setPrintAcceptanceData(undefined);

            const blobURL = URL.createObjectURL(acceptancePdfInstance.blob);
            printJS(blobURL);
        }
    }, [startPrintAcceptance, acceptancePdfInstance]);

    const getPrintData = () => {
        setLoadingPrintData(true);

        serverFetch(`warehouse/acceptance/${currentTruck?.id}/printbarcode`, { method: 'GET' })
            .then((data: any) => {
                setPrintData(data);
            })
            .catch((ex: any) => {
                setLoadingPrintData(false);
                exception(api, 'Ошибка получения груза', ex, () => d(userLoaded(undefined)));
            });
    };

    const getPrintAcceptanceData = () => {
        setLoadingPrintAcceptanceData(true);

        serverFetch(`trucks/${currentTruck?.id}/print`, { method: 'GET' })
            .then((data: any) => {
                const printData = {
                    truckNumber: currentTruck?.number,
                    consignments: data,
                };
                setPrintAcceptanceData(printData);
            })
            .catch((ex: any) => {
                setLoadingPrintAcceptanceData(false);
                exception(api, 'Ошибка получения груза', ex, () => d(userLoaded(undefined)));
            });
    };

    const generateBoxes = () => {
        setBarcodesGenerating(true);

        serverFetch(`warehouse/acceptance/${currentTruck?.id}/generateboxes`, { method: 'POST' })
            .then(() => {
                success(api, 'Генерация штрих-кодов', `Успешно`);

                setBarcodesGenerating(false);
                setRefreshRequired(true);
            })
            .catch((ex: any) => {
                setBarcodesGenerating(false);
                exception(api, 'Ошибка генерации штрих-кодов', ex, () => d(userLoaded(undefined)));
            });
    };

    const onComplete = () => {
        if (!currentTruck) return;

        serverFetch(`warehouse/acceptance/${currentTruck?.id}/complete`, { method: 'POST' })
            .then(() => {
                onSelectChange([]);
                setRefreshRequired(true);
            })
            .catch((ex: any) => {
                exception(api, 'Ошибка завершения приемки', ex, () => d(userLoaded(undefined)));
            });
    };

    const onRevertOnway = () => {
        if (!currentTruck) return;

        serverFetch(`warehouse/acceptance/${currentTruck.id}/revert`, { method: 'POST' })
            .then(() => {
                d(setFilter({ ...filter, statuses: [TruckStatus.OnWay], isArchived: false }, filterContext));
            })
            .catch((ex: any) => {
                setLoading(false);
                exception(api, 'Ошибка изменения статуса', ex, () => d(userLoaded(undefined)));
            });
    };

    const renderToolbar = () => {
        return (
            <Toolbar
                commands={[
                    {
                        label: 'Обновить',
                        key: 'refresh',
                        disabled: loading,
                        icon: <ReloadOutlined />,
                        onClick: () => {
                            setRefreshRequired(true);
                        },
                    },
                    { isDivider: true },
                    {
                        label: 'Детали поставки',
                        key: 'delivery-details',
                        disabled: !currentTruck,
                        icon: <UnorderedListOutlined />,
                        onClick: () => {
                            const canPrint = currentTruck?.consignments?.every((e) => e.hasBoxNumbers);
                            if (!canPrint) {
                                error(api, 'Сгенерируйте штрих-коды');
                                return;
                            }

                            navigate(`${resolved.pathname}/${currentTruck?.id}`);
                        },
                    },
                    { isDivider: true },
                    {
                        label: 'Сгенерировать штрих-коды',
                        key: 'generateBarcode',
                        disabled:
                            !currentTruck ||
                            currentTruck.status != TruckStatus.OnWay ||
                            currentTruck?.consignments?.every((e) => e.hasBoxNumbers),
                        icon: <BarcodeOutlined />,
                        onClick: () => {
                            if (!hasPermission(userSession.permissions, Permission.ManageWarehouse)) {
                                securityRestriction(api, [Permission.ManageWarehouse]);
                                return;
                            }

                            generateBoxes();
                        },
                    },
                    {
                        label: 'Печатать штрих-коды',
                        key: 'printBarcode',
                        disabled: loading || !currentTruck || currentTruck.status != TruckStatus.OnWay || startPrint,
                        icon: <PrinterOutlined />,
                        onClick: () => {
                            if (!hasPermission(userSession.permissions, Permission.ManageWarehouse)) {
                                securityRestriction(api, [Permission.ManageWarehouse]);
                                return;
                            }

                            const canPrint = currentTruck?.consignments?.every((e) => e.hasBoxNumbers);
                            if (!canPrint) {
                                error(api, 'Сгенерируйте штрих-коды');
                                return;
                            }

                            getPrintData();
                        },
                    },
                    {
                        label: 'Назначить исполнителя',
                        key: 'assignJob',
                        disabled: !currentTruck || currentTruck.status != TruckStatus.OnWay,
                        icon: <UsergroupAddOutlined />,
                        onClick: () => {
                            if (!hasPermission(userSession.permissions, Permission.ManageWarehouse)) {
                                securityRestriction(api, [Permission.ManageWarehouse]);
                                return;
                            }

                            const canPrint = currentTruck?.consignments?.every((e) => e.hasBoxNumbers);
                            if (!canPrint) {
                                error(api, 'Сгенерируйте штрих-коды');
                                return;
                            }

                            navigate(`/flp/warehouse/performers/${AreaType.Acceptance}/${currentTruck?.id}`);
                        },
                    },
                    { isDivider: true },
                    {
                        label: 'Печать листа сверки',
                        key: 'print',
                        disabled: loading || !currentTruck || startPrint,
                        icon: <PrinterOutlined />,
                        onClick: () => {
                            if (!hasPermission(userSession.permissions, Permission.PrintDocuments)) {
                                securityRestriction(api, [Permission.PrintDocuments]);
                                return;
                            }

                            getPrintAcceptanceData();
                        },
                    },
                    { isDivider: true },
                    {
                        label: 'Завершить приемку',
                        key: 'complete-acceptance',
                        disabled: !currentTruck || currentTruck.status != TruckStatus.OnWay,
                        type: 'primary',
                        icon: <CheckOutlined />,
                        onClick: () => {
                            if (!hasPermission(userSession.permissions, Permission.CompleteAcceptanceForce)) {
                                securityRestriction(api, [Permission.CompleteAcceptanceForce]);
                                return;
                            }

                            modal.confirm({
                                title: `Завершить приемку машины принудительно "${currentTruck?.number}"?`,
                                okType: 'primary',
                                icon: <QuestionCircleOutlined />,
                                okText: 'ОК',
                                cancelText: 'Отмена',
                                onOk: () => {
                                    onComplete();
                                },
                            });
                        },
                    },
                    {
                        label: 'Вернуть в работу',
                        key: 'back-onway',
                        hidden:
                            !hasPermission(userSession.permissions, Permission.RevertAcceptance) ||
                            !currentTruck ||
                            currentTruck.status != TruckStatus.Finished,
                        type: 'primary',
                        icon: <RollbackOutlined />,
                        onClick: () => {
                            if (!hasPermission(userSession.permissions, Permission.FullAccess)) {
                                securityRestriction(api, [Permission.FullAccess]);
                                return;
                            }

                            modal.confirm({
                                title: `Изменить статус машины на "В Пути"?`,
                                okType: 'primary',
                                icon: <QuestionCircleOutlined />,
                                okText: 'ОК',
                                cancelText: 'Отмена',
                                onOk: () => {
                                    onRevertOnway();
                                },
                            });
                        },
                    },
                ]}
                filter={{
                    active: showFilter,
                    onHideFilter: () => {
                        setShowFilter(!showFilter);
                    },
                }}
            />
        );
    };

    const renderFilter = () => {
        return (
            <Filter
                display={showFilter}
                items={[
                    <Input
                        style={{ width: 150 }}
                        key='number'
                        placeholder='Машина'
                        value={filter?.number}
                        onChange={(data) => {
                            d(setFilter({ ...filter, number: data.target.value }, filterContext));
                        }}
                    />,
                    <InputNumber
                        style={{ width: 130 }}
                        key='consigneeCode'
                        placeholder='ID клиента'
                        value={filter?.consigneeCode}
                        onChange={(value) => {
                            d(setFilter({ ...filter, consigneeCode: value }, filterContext));
                        }}
                    />,
                    <Input
                        style={{ width: 130 }}
                        key='markingCode'
                        placeholder='Клиент'
                        value={filter?.markingCode}
                        onChange={(data) => {
                            d(setFilter({ ...filter, markingCode: data.target.value }, filterContext));
                        }}
                    />,
                    <Input
                        style={{ width: 130 }}
                        key='awbNumber'
                        placeholder='AWB'
                        value={filter?.awbNumber}
                        onChange={(data) => {
                            d(setFilter({ ...filter, awbNumber: data.target.value }, filterContext));
                        }}
                    />,
                    <Select
                        key='status'
                        placeholder='Статус машины'
                        value={filter?.statuses}
                        size='middle'
                        allowClear
                        style={{ width: 200 }}
                        onChange={(value) => d(setFilter({ ...filter, statuses: [value] }, filterContext))}
                        options={statuses.map((s) => {
                            return { value: s.value, label: s.label };
                        })}
                    />,
                    <Form.Item key='isArchived' label='Архив' style={{ margin: 0 }}>
                        <Switch
                            checked={filter?.isArchived}
                            onChange={(value: any) => {
                                d(setFilter({ ...filter, statuses: value ? [] : [TruckStatus.OnWay], isArchived: value }, filterContext));
                            }}
                        />
                    </Form.Item>,
                ]}
                onClear={() => d(setFilter(initFilter, filterContext))}
            />
        );
    };

    const onSelectChange = (selectedRowKeys: React.Key[]) => {
        setSelectedIds(selectedRowKeys);

        if (selectedRowKeys.length == 1) {
            const item = headers.find((o) => o.id === selectedRowKeys[0]);
            setCurrentTruck(item);
        } else {
            setCurrentTruck(undefined);
        }
    };

    const onExpandCities = (expanded: boolean, record: ICity) => {
        let cityKeys = [...expandedCityKeys];

        const key = record?.cityKey as React.Key;
        if (expanded) {
            cityKeys.push(key);
        } else {
            cityKeys = cityKeys.filter((k) => k != key);
        }

        setExpandedCityKyes(cityKeys);
    };

    const onExpandTruks = (expanded: boolean, record: ITruck) => {
        if (!expanded) {
            setExpandedCityKyes(expandedCityKeys.filter((k) => !(k as string).includes(record.id || '')));
        }
    };

    const expandedConsignments = (record: ICity) => {
        const consignments = record.consignments?.map((c) => {
            const boxGroups: any = [];
            let totalQty: number = 0;
            let totalScannedQty: number = 0;

            c.boxGroups?.map((b) => {
                if (!b.countryId) return;

                boxGroups[b.countryId] = { qty: b.qty, scannedBoxQty: b.scannedBoxQty, awbNumber: b.awbNumber };
                ++totalQty;
                totalScannedQty += b.scannedBoxQty || 0;
            });

            c = {
                ...c,
                ...boxGroups,
                totalQty: totalQty,
                totalScannedQty: totalScannedQty,
            };
            return c;
        });

        const columns: ColumnsType<IConsignment> = [
            {
                title: 'ID',
                dataIndex: 'consigneeCode',
                align: 'center',
                width: 60,
                render: (_, record) => {
                    return (
                        <>
                            {record.consigneeCode}
                            <TransitWarehouseTag warehouseCode={record.warehouseCode} />
                        </>
                    );
                },
            },
            {
                title: 'Клиент',
                width: 300,
                render: (_, record) => {
                    return (
                        <div>
                            <div> {record.userName}</div>
                            <div style={{ fontWeight: 500, fontSize: 16 }}> {record.consigneeKeyName}</div>
                        </div>
                    );
                },
            },
            {
                dataIndex: 'countries',
                width: 375,
                render: (_, record) => {
                    return (
                        <Flex gap='small'>
                            {countries.map((c) => {
                                const boxGroups = record.boxGroups?.filter((bg) => bg.countryId === c.id);
                                if (!boxGroups || boxGroups.length <= 0) return undefined;

                                return (
                                    <ConsignmentCard
                                        key={uuid()}
                                        country={c}
                                        api={api}
                                        items={boxGroups as Array<IBoxGroupCardItem>}
                                        allowComment={true}
                                        onRefresh={() => setRefreshRequired(true)}
                                        serverFetch={serverFetch}
                                    />
                                );
                            })}
                        </Flex>
                    );
                },
            },
            {
                title: 'Кол-во / шт',
                align: 'center',
                width: 120,
                dataIndex: 'totalQty',
                onCell: (record) => ({
                    style: {
                        background: '#fafafa',
                        fontWeight: 600,
                    },
                }),
                render: (_, record) => {
                    return (
                        <>
                            <span style={{ fontWeight: 600, color: record.totalScannedQty === record.totalQty ? '#228B22' : 'red' }}>
                                {record.totalScannedQty}
                            </span>
                            <span> / </span>
                            <span>{record.totalQty}</span>
                        </>
                    );
                },
            },
            {},
        ];

        return <Table rowKey='id' size='small' columns={columns} dataSource={consignments} pagination={false} />;
    };

    const expandedCities = (record: ITruck) => {
        if (!record || !record.totalQty || record.totalQty <= 0) {
            //  warning(api, "Пожалуйста сгенерируйте штрих-коды")
            return;
        }

        const cities: Array<ICity> = [];
        const cityNames: Array<string> = [];

        record.consignments?.map((e) => {
            let city = cities.find((c) => c.name === e.cityName && c.tag === e.tag);
            if (!city) {
                city = {
                    cityKey: `${e.truckId}#${e.cityName}#${e.tag}`,
                    name: e.cityName,
                    tag: e.tag,
                    totalQty: 0,
                    totalScannedQty: 0,
                    consignments: [],
                };
                cities.push(city);
                cityNames.push(e.cityName || '');
            }

            e.boxGroups?.map((b) => {
                if (city) {
                    if (!city.totalQty) city.totalQty = 0;
                    if (!city.totalScannedQty) city.totalScannedQty = 0;

                    ++city.totalQty;
                    city.totalScannedQty += b.scannedBoxQty || 0;
                }
            });

            city.consignments?.push(e);
        });

        const columns: ColumnsType<ICity> = [
            {
                title: 'Город доставки',
                width: 730,
                onCell: () => ({
                    style: {
                        fontWeight: 700,
                    },
                }),
                render: (_, record) => {
                    return (
                        <Flex>
                            <span>{record.name}</span>
                            <Tags label={record.tag} />
                        </Flex>
                    );
                },
            },
            {
                title: 'Кол-во / шт',
                align: 'center',
                width: 120,
                onCell: () => ({
                    style: {
                        background: '#fff8d5',
                        fontWeight: 700,
                    },
                }),
                render: (_, record) => {
                    return (
                        <>
                            <span style={{ color: record.totalScannedQty === record.totalQty ? '#228B22' : 'red' }}>
                                {record.totalScannedQty}
                            </span>
                            <span> / </span>
                            <span>{record.totalQty}</span>
                        </>
                    );
                },
            },
            {},
        ];

        return (
            <Table
                rowKey='cityKey'
                size='small'
                columns={columns}
                dataSource={cities}
                pagination={false}
                expandable={{
                    columnWidth: 32,
                    expandedRowRender: expandedConsignments,
                    expandedRowKeys: expandedCityKeys,
                    onExpand: onExpandCities,
                }}
            />
        );
    };

    const renderTable = () => {
        const columns: ColumnsType<ITruck> = [
            {
                title: 'Машина',
                dataIndex: 'number',
                key: 'number',
                width: 150,
            },
            {
                title: 'Отгрузка',
                width: 100,
                render: (_, record) => {
                    return getDate(record.departureOn);
                },
            },
            {
                title: 'Кол-во / шт',
                width: 120,
                align: 'center',
                onCell: () => ({
                    style: {
                        background: '#FFEE96',
                        fontWeight: 700,
                    },
                }),
                render: (_, record) => {
                    return record.consignments?.every((e) => e.hasBoxNumbers) ? (
                        <>
                            <span style={{ fontWeight: 600, color: record.totalScannedQty === record.totalQty ? '#228B22' : 'red' }}>
                                {record.totalScannedQty}
                            </span>
                            <span> / </span>
                            <span>{record.totalQty}</span>
                        </>
                    ) : (
                        <Tooltip placement='topRight' title='Сгенерируйте штрих-коды'>
                            <ExclamationCircleOutlined />
                        </Tooltip>
                    );
                },
            },

            {
                align: 'center',
                width: 40,
                render: (_, record) => {
                    return record.consignments?.every((e) => e.hasBoxNumbers) && <BarcodeOutlined />;
                },
            },
            {
                title: 'Исполнитель',
                key: 'performerName',
                dataIndex: 'performerName',
                width: 200,
            },
            {
                title: 'Статус выполнения',
                key: 'jobStatus',
                width: 160,
                render: (_, record) => {
                    return <>{jobStatusLabel(record.jobStatus)} </>;
                },
            },
            {},
        ];

        return (
            <Table
                rowKey='id'
                size='small'
                loading={{
                    spinning: loading,
                    indicator: <LoadingOutlined style={{ fontSize: 44 }} spin />,
                }}
                columns={columns}
                expandable={{
                    columnWidth: 32,
                    expandedRowRender: expandedCities,
                    onExpand: onExpandTruks,
                }}
                dataSource={headers}
                rowSelection={{
                    selectedRowKeys: selectedIds,
                    onChange: onSelectChange,
                    type: 'radio',
                    columnWidth: 32,
                }}
                onRow={(record) => {
                    return {
                        onClick: () => {
                            onSelectChange([record.id || '']);
                        },
                    };
                }}
                pagination={false}
                scroll={{ y: height }}
                virtual={true}
            />
        );
    };

    return (
        <div ref={containerRef} style={{ height: `calc(100vh - 233px - ${filterHeight}px + ${showFilter ? 0 : 16}px)` }}>
            {renderToolbar()}
            {renderFilter()}
            {renderTable()}

            {(loadingPrintData || loadingPrintAcceptanceData || startPrintAcceptance || startPrint) && <Processing title='Печать...' />}
            {barcodesGenerating && <Processing title='Генерация штрих-кодов...' />}

            {contextHolder}
            {modalContextHolder}
        </div>
    );
};

export default Acceptance;
